/**
* @Author: Michael Neumair <mBook>
* @Date:   2016-09-22T17:40:29+02:00
* @Email:  7q7w7e7r@gmail.com
* @Last modified by:   mBook
* @Last modified time: 2016-10-06T19:49:43+02:00
*/

import warning from 'warning';

const emptyString = '';
const month = 'M';
const day = 'D';
const year = 'Y';
const extendedMonth = 'MM';
const extendedDay = 'DD';
const extendedYear = 'YYYY';
const digitRegEx = /\d/;
const defaultFormat = 'M.D.Y';
const twoDigitsRegEx = /[0-9]{2}/;
const fourDigitsRegEx = /[0-9]{4}/;
const dayRegEx = /D/g;
const monthRegEx = /M/g;
const yearRegEx = /Y/g;


/**
 * checks if the dateFormat is valid, otherwise it throws an error
 * for development
 */
function checkDateFormat(dateFormat) {
  // check if date format is complete with M, Y, D!
  const matchDay = dateFormat.match(dayRegEx) || [];
  const matchMonth = dateFormat.match(monthRegEx) || [];
  const matchYear = dateFormat.match(yearRegEx) || [];

  warning(
      matchDay.length === 1 && matchMonth.length === 1 && matchYear.length === 1,
      `Wrong date format! Expecting something like 'D/M/Y' but got '${dateFormat}'`
  );
}


/**
 * parses a date format ('M/D/Y') and creates a
 * mask array out of it
 * @param  {String} dateFormat
 *              something like 'M/D/Y'
 * @return {Array} mask
 *              [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
 */
function dateFormatToMask(dateFormat) {
  const length = dateFormat.length;
  const mask = [];
  for (let i = 0; i < length; i++) {
    const char = dateFormat[i];
    switch (char) {
      case day:
      case month:
        mask.push(digitRegEx);
        mask.push(digitRegEx);
        break;

      case year:
        mask.push(digitRegEx);
        mask.push(digitRegEx);
        mask.push(digitRegEx);
        mask.push(digitRegEx);
        break;

      default:
        mask.push(char);
    }
  }
  return mask;
}


/**
 * helper function, which extends the dateFormat 'D.M.Y'
 * to its full representation 'DD.MM.YYYY'
 * @param  {String} dateFormat
 *              a date format like 'D.M.Y' or 'M/D/Y'
 * @return {String} extendedFormat
 *              the dat format in its full char length representation
 *              like 'DD.MM.YYY' or 'MM/DD/YYYY'
 */
function extendDateFormat(dateFormat) {
  checkDateFormat(dateFormat);

  const length = dateFormat.length;
  let extendedFormat = '';
  for (let i = 0; i < length; i++) {
    const char = dateFormat[i];
    switch (char) {
      case day:
        extendedFormat = `${extendedFormat}${extendedDay}`;
        break;

      case month:
        extendedFormat = `${extendedFormat}${extendedMonth}`;
        break;

      case year:
        extendedFormat = `${extendedFormat}${extendedYear}`;
        break;

      default:
        extendedFormat = `${extendedFormat}${char}`;
    }
  }

  return extendedFormat;
}


/**
 * creates the date mask based on the dateFormat
 * @param {String} dateFormat
 *        accepts a value like Y/M/D
 *        or D.M.Y ...
 *        it is always the full year, full day
 * @type {String}
 */
export function createDateMask({
  dateFormat = defaultFormat,
} = {}) {
  return dateFormatToMask(dateFormat);
}


export function createDateParser({
  dateFormat = defaultFormat,
} = {}) {
  checkDateFormat(dateFormat);
  const extendedFormat = extendDateFormat(dateFormat);

  return (inputValue) => {
    if (typeof inputValue !== 'string' || inputValue === emptyString) {
      if ( inputValue && ( typeof inputValue.toDateString == 'function' ) ) {
        // this is already parsed date Object
        return inputValue;
      } else {
        // if we do not have a valid string value or date object, return null
        return null;
      }
    }

    if (inputValue.length !== extendedFormat.length) {
      // we can't parse the value, as it isn't a complete inputValue
      return null;
    }

    const monthIndex = extendedFormat.search(extendedMonth);
    const dayIndex = extendedFormat.search(extendedDay);
    const yearIndex = extendedFormat.search(extendedYear);

    if (
      monthIndex === -1
            || dayIndex === -1
            || yearIndex === -1
    ) {
      return null; // something is wrong :)
    }

    const monthSlice = inputValue.slice(monthIndex, monthIndex + extendedMonth.length);
    const daySlice = inputValue.slice(dayIndex, dayIndex + extendedDay.length);
    const yearSlice = inputValue.slice(yearIndex, yearIndex + extendedYear.length);

    if (
      !~monthSlice.search(twoDigitsRegEx)
            || !~daySlice.search(twoDigitsRegEx)
            || !~yearSlice.search(fourDigitsRegEx)
    ) {
      return null;
    }

    const date = new Date(Date.parse(`${yearSlice}-${monthSlice}-${daySlice}`));

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date;
  };
}


function fillZeros(value, expectedLength = 2) {
  const valueLength = value.length;
  const diff = expectedLength - valueLength;
  let extendedValue = value;
  for (let i = 0; i < diff; i++) {
    extendedValue = `0${extendedValue}`;
  }
  return extendedValue;
}

export function createDateFormatter({
  dateFormat = defaultFormat,
} = {}) {
  checkDateFormat(dateFormat);

  return (date) => {
    const isDate = date && typeof date === 'object' && date.getTime && !Number.isNaN(date.getTime());
    const isNumber = typeof date === 'number' && !Number.isNaN(date);

    if (!isDate && !isNumber) {
      return null;
    }

    if (isNumber) {
      date = new Date(date); // eslint-disable-line no-param-reassign
    }

    const length = dateFormat.length;
    let formattedDate = '';
    for (let i = 0; i < length; i++) {
      const char = dateFormat[i];
      switch (char) {
        case day: {
          const dayString = String(date.getDate());
          formattedDate = `${formattedDate}${fillZeros(dayString, 2)}`;
          break;
        }

        case month: {
          const monthString = String(date.getMonth() + 1);
          formattedDate = `${formattedDate}${fillZeros(monthString, 2)}`;
          break;
        }

        case year: {
          const yearString = String(date.getFullYear());
          formattedDate = `${formattedDate}${fillZeros(yearString, 4)}`;
          break;
        }

        default:
          formattedDate = `${formattedDate}${char}`;
      }
    }
    return formattedDate;
  };
}


export default {
  createDateMask,
  createDateParser,
  createDateFormatter,
};

